// dependencies
import { cva } from "class-variance-authority";
import React, { forwardRef } from "react";

//icons
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { faCircleXmark } from "@performyard/fontawesome/pro-solid-svg-icons";

//utils
import { cn } from "../../../lib/utils";

//theme
import { BaseTopColors } from "../../../theme";
// components
import { ButtonIcon, Icon, IconProps } from "../../atoms";
import { AvatarProps } from "../Avatar";
import { AvatarProfile } from "../AvatarProfile";

interface BaseChipProps {
	/**
	 * The icon to display on the left side of the chip
	 */
	icon?: IconDefinition | React.ReactElement<typeof Icon>;
	/**
	 *
	 * The function to call when the dismiss button is clicked, also determines if a dismiss button is rendered
	 */
	onDismiss?: () => void;
	/**
	 * The text to display in the chip
	 */
	label?: string | React.ReactNode;
	/**
	 * The color of the chip
	 */
	color: BaseTopColors;
	/**
	 * The size of the chip
	 */
	size: 20 | 24 | 28;
	/**
	 * If the chip is selected
	 */
	selected?: boolean;
	/**
	 * The class name to apply to the chip
	 */
	dangerouslySetClassName?: string;
	/**
	 * The employee id to display the avatar, also determines whether or not to render AvatarProfile
	 */
	employeeId?: string;
	/**
	 * The employee name to display the avatar
	 */
	employeeName?: string;
}

// Defines props so that employeeId is required if employeeName is passed and vice versa
export type ChipProps =
	| (BaseChipProps & { employeeId: string; employeeName: string })
	| (BaseChipProps & { employeeId?: undefined; employeeName?: undefined });

const chipVariants = cva("inline-flex items-center justify-center gap-0.5 rounded-[50px] py-0.5", {
	variants: {
		color: {
			primary: "bg-primary-1",
			neutral: "bg-neutral-2",
			red: "bg-red-1",
			green: "bg-green-1",
			yellow: "bg-yellow-1",
		},
		size: {
			20: "h-4 text-caption font-regular",
			24: "h-5 text-caption font-regular",
			28: "h-6 text-paragraph-3 font-regular",
		},
		selected: {
			true: "text-neutral-0",
			false: "text-neutral-9",
		},
	},
	compoundVariants: [
		{
			selected: true,
			color: "primary",
			className: "bg-primary-5",
		},
		{
			selected: true,
			color: "neutral",
			className: "bg-neutral-5",
		},
		{
			selected: true,
			color: "red",
			className: "bg-red-5",
		},
		{
			selected: true,
			color: "green",
			className: "bg-green-5",
		},
		{
			selected: true,
			color: "yellow",
			className: "bg-yellow-5",
		},
	],
	defaultVariants: {
		size: 24,
	},
});

const iconSizeMap: { [K in ChipProps["size"]]: IconProps["size"] } = {
	20: 16,
	24: 16,
	28: 24,
};

const avatarSizeMap: { [K in ChipProps["size"]]: AvatarProps["size"] } = {
	20: 16,
	24: 20,
	28: 24,
};

const iconColorMap: { [K in ChipProps["color"]]: IconProps["color"] } = {
	primary: "primary-5",
	green: "green-5",
	red: "red-5",
	neutral: "neutral-5",
	yellow: "yellow-5",
};

/**
 * A Chip component, used to display a small amount of information in a compact format
 * has a dismiss button and avatarProfile functionality
 */
const Chip = forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
	const { color, size, selected, icon, onDismiss, label, employeeId, employeeName } = props;

	const hasAvatar = !!employeeId;
	// per figma design, padding is adjusted based on the presence of an icon, avatar, and label
	let leftPadding = "pl-[6px]";
	if (icon) {
		leftPadding = "pl-[4px]";
	} else if (hasAvatar && !label && !icon) {
		leftPadding = "pl-0";
	}

	let rightPadding = "pr-[6px]";
	if (onDismiss) {
		rightPadding = "pr-[4px]";
	}

	const calculatedBaseClassName = `${leftPadding} ${rightPadding}`;

	const iconComponent =
		React.isValidElement(icon) || !icon ? (
			icon
		) : (
			<Icon icon={icon} color={selected ? "neutral-0" : iconColorMap[color]} />
		);

	return (
		<div
			ref={ref}
			className={cn(
				chipVariants({ color, size, selected }),
				props.dangerouslySetClassName,
				calculatedBaseClassName
			)}
		>
			{icon && iconComponent}
			<div className="min-w-0 truncate">{label}</div>
			{hasAvatar && (
				<AvatarProfile
					employeeId={employeeId}
					employeeName={employeeName}
					avatarSize={avatarSizeMap[size]}
					noLink
					textColor={selected ? "neutral-0" : "neutral-9"}
				/>
			)}
			{onDismiss && (
				<ButtonIcon
					icon={faCircleXmark}
					color={color}
					shade={3}
					size={iconSizeMap[size]}
					variant="standard"
					onClick={onDismiss}
					data-testid="chip-dismiss-button"
					dangerouslySetClassName={
						selected
							? "text-neutral-0 hover:opacity-80 active:opacity-80 hover:text-neutral-0 active:text-neutral-0 "
							: ""
					}
				/>
			)}
		</div>
	);
});

Chip.displayName = "Chip";

export { Chip };
